import { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { CalendarIcon, Loader2, Utensils, Trash2, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { NutritionHistory } from './NutritionHistory';
import { BrandAutocomplete } from './BrandAutocomplete';

interface HistoryRecord {
  id: string;
  diet_start_date: string | null;
  diet_end_date: string | null;
}

interface NutritionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  petId: string;
  petName: string;
}

type FoodType = 'dry_kibble' | 'wet_food' | 'barf' | 'homemade' | 'prescription' | 'grass' | 'hay' | 'forage' | 'herbs' | 'silage' | 'concentrate' | 'corn' | 'wheat' | 'barley' | 'soy_meal' | 'sunflower_meal' | 'mixed_grains' | 'concentrate_poultry' | 'concentrate_swine';
type FeedingFrequency = 'once_daily' | 'twice_daily' | 'three_times' | 'free_feeding';

interface NutritionData {
  id?: string;
  food_type: FoodType;
  brand: string;
  product_name: string;
  feeding_frequency: FeedingFrequency;
  portion_size: string;
  diet_start_date: Date | null;
  diet_end_date: Date | null;
  notes: string;
}

const FOOD_TYPES: { value: FoodType; labelKey: string }[] = [
  // Pet food types
  { value: 'dry_kibble', labelKey: 'nutrition.foodTypes.dryKibble' },
  { value: 'wet_food', labelKey: 'nutrition.foodTypes.wetFood' },
  { value: 'barf', labelKey: 'nutrition.foodTypes.barf' },
  { value: 'homemade', labelKey: 'nutrition.foodTypes.homemade' },
  { value: 'prescription', labelKey: 'nutrition.foodTypes.prescription' },
  // Farm animal food types (herbivores)
  { value: 'grass', labelKey: 'nutrition.foodTypes.grass' },
  { value: 'hay', labelKey: 'nutrition.foodTypes.hay' },
  { value: 'forage', labelKey: 'nutrition.foodTypes.forage' },
  { value: 'herbs', labelKey: 'nutrition.foodTypes.herbs' },
  { value: 'silage', labelKey: 'nutrition.foodTypes.silage' },
  { value: 'concentrate', labelKey: 'nutrition.foodTypes.concentrate' },
  // Cereals (poultry/swine)
  { value: 'corn', labelKey: 'nutrition.foodTypes.corn' },
  { value: 'wheat', labelKey: 'nutrition.foodTypes.wheat' },
  { value: 'barley', labelKey: 'nutrition.foodTypes.barley' },
  { value: 'mixed_grains', labelKey: 'nutrition.foodTypes.mixedGrains' },
  // By-products
  { value: 'soy_meal', labelKey: 'nutrition.foodTypes.soyMeal' },
  { value: 'sunflower_meal', labelKey: 'nutrition.foodTypes.sunflowerMeal' },
  // Species-specific concentrates
  { value: 'concentrate_poultry', labelKey: 'nutrition.foodTypes.concentratePoultry' },
  { value: 'concentrate_swine', labelKey: 'nutrition.foodTypes.concentrateSwine' },
];

const FEEDING_FREQUENCIES: { value: FeedingFrequency; labelKey: string }[] = [
  { value: 'once_daily', labelKey: 'nutrition.frequency.onceDaily' },
  { value: 'twice_daily', labelKey: 'nutrition.frequency.twiceDaily' },
  { value: 'three_times', labelKey: 'nutrition.frequency.threeTimes' },
  { value: 'free_feeding', labelKey: 'nutrition.frequency.freeFeeding' },
];

const getInitialFormData = (): NutritionData => ({
  food_type: 'dry_kibble',
  brand: '',
  product_name: '',
  feeding_frequency: 'twice_daily',
  portion_size: '',
  diet_start_date: null,
  diet_end_date: null,
  notes: '',
});

export const NutritionModal = ({ open, onOpenChange, petId, petName }: NutritionModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');
  const [formData, setFormData] = useState<NutritionData>(getInitialFormData());
  const [historyRecords, setHistoryRecords] = useState<HistoryRecord[]>([]);

  // Load existing nutrition data
  const loadNutrition = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('pet_nutrition')
        .select('*')
        .eq('pet_id', petId)
        .eq('is_current', true)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setFormData({
          id: data.id,
          food_type: data.food_type as FoodType,
          brand: data.brand || '',
          product_name: data.product_name || '',
          feeding_frequency: data.feeding_frequency as FeedingFrequency,
          portion_size: data.portion_size || '',
          diet_start_date: data.diet_start_date ? new Date(data.diet_start_date + 'T00:00:00') : null,
          diet_end_date: (data as { diet_end_date?: string }).diet_end_date ? new Date((data as { diet_end_date?: string }).diet_end_date + 'T00:00:00') : null,
          notes: data.notes || '',
        });
      } else {
        setFormData(getInitialFormData());
      }
    } catch (error: unknown) {
      console.error('Error loading nutrition:', error);
    } finally {
      setIsLoading(false);
    }
  }, [petId]);

  // Load history records for date validation
  const loadHistory = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('pet_nutrition_history')
        .select('id, diet_start_date, diet_end_date')
        .eq('pet_id', petId)
        .order('diet_end_date', { ascending: false });

      if (error) throw error;
      setHistoryRecords(data || []);
    } catch (error) {
      console.error('Error loading history for validation:', error);
    }
  }, [petId]);

  useEffect(() => {
    if (open && petId) {
      loadNutrition();
      loadHistory();
    }
  }, [open, petId, loadNutrition, loadHistory]);

  // Validate dates against history records and end date consistency
  const dateValidationError = useMemo(() => {
    // Validate end date is after start date
    if (formData.diet_start_date && formData.diet_end_date) {
      if (formData.diet_end_date < formData.diet_start_date) {
        return t('nutrition.dateEndBeforeStartError');
      }
    }
    
    if (!formData.diet_start_date) return null;
    
    const newStart = formData.diet_start_date;
    const newEnd = formData.diet_end_date;
    
    for (const record of historyRecords) {
      if (!record.diet_start_date || !record.diet_end_date) continue;
      
      const recordStart = parseISO(record.diet_start_date);
      const recordEnd = parseISO(record.diet_end_date);
      
      // Check if new start date falls within an existing range
      if (newStart >= recordStart && newStart <= recordEnd) {
        return t('nutrition.dateOverlapError', {
          start: format(recordStart, 'dd/MM/yyyy'),
          end: format(recordEnd, 'dd/MM/yyyy')
        });
      }
      
      // Check if new end date falls within an existing range
      if (newEnd && newEnd >= recordStart && newEnd <= recordEnd) {
        return t('nutrition.dateOverlapError', {
          start: format(recordStart, 'dd/MM/yyyy'),
          end: format(recordEnd, 'dd/MM/yyyy')
        });
      }
      
      // Check if new range encompasses an existing range
      if (newEnd && newStart <= recordStart && newEnd >= recordEnd) {
        return t('nutrition.dateOverlapError', {
          start: format(recordStart, 'dd/MM/yyyy'),
          end: format(recordEnd, 'dd/MM/yyyy')
        });
      }
    }
    
    // Check if date is before the last history record's end date
    const lastRecord = historyRecords[0]; // Already sorted by diet_end_date desc
    if (lastRecord?.diet_end_date) {
      const lastEnd = parseISO(lastRecord.diet_end_date);
      if (newStart < lastEnd) {
        return t('nutrition.dateBeforeHistoryError', {
          date: format(lastEnd, 'dd/MM/yyyy')
        });
      }
    }
    
    return null;
  }, [formData.diet_start_date, formData.diet_end_date, historyRecords, t]);

  const handleSave = async () => {
    if (!user) return;
    if (dateValidationError) {
      toast.error(dateValidationError);
      return;
    }
    
    setIsSaving(true);
    try {
      const nutritionData = {
        pet_id: petId,
        user_id: user.id,
        food_type: formData.food_type,
        brand: formData.brand || null,
        product_name: formData.product_name || null,
        feeding_frequency: formData.feeding_frequency,
        portion_size: formData.portion_size || null,
        diet_start_date: formData.diet_start_date ? format(formData.diet_start_date, 'yyyy-MM-dd') : null,
        diet_end_date: formData.diet_end_date ? format(formData.diet_end_date, 'yyyy-MM-dd') : null,
        notes: formData.notes || null,
        is_current: true,
      };

      if (formData.id) {
        // Update existing
        const { error } = await supabase
          .from('pet_nutrition')
          .update(nutritionData)
          .eq('id', formData.id);

        if (error) throw error;
        toast.success(t('nutrition.updateSuccess'));
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('pet_nutrition')
          .insert(nutritionData)
          .select()
          .single();

        if (error) throw error;
        
        // Update the form data with the new ID so subsequent saves are updates
        if (data) {
          setFormData(prev => ({ ...prev, id: data.id }));
        }
        toast.success(t('nutrition.createSuccess'));
      }

      // Reload to ensure we have the latest data
      await loadNutrition();
    } catch (error: unknown) {
      console.error('Error saving nutrition:', error);
      toast.error(t('nutrition.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.id || !user) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('pet_nutrition')
        .delete()
        .eq('id', formData.id)
        .eq('user_id', user.id);

      if (error) throw error;
      
      toast.success(t('nutrition.deleteSuccess'));
      setFormData(getInitialFormData());
    } catch (error: unknown) {
      console.error('Error deleting nutrition:', error);
      toast.error(t('nutrition.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Utensils className="h-5 w-5 text-primary" />
            {t('nutrition.title', { petName })}
          </DialogTitle>
          <DialogDescription>
            {t('nutrition.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            className={cn(
              "pb-2 px-3 text-sm font-medium transition-colors",
              activeTab === 'current' 
                ? "border-b-2 border-primary text-primary" 
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => setActiveTab('current')}
          >
            {t('nutrition.currentDiet')}
          </button>
          <button
            className={cn(
              "pb-2 px-3 text-sm font-medium transition-colors",
              activeTab === 'history' 
                ? "border-b-2 border-primary text-primary" 
                : "text-muted-foreground hover:text-foreground"
            )}
            onClick={() => setActiveTab('history')}
          >
            {t('nutrition.history')}
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : activeTab === 'current' ? (
          <div className="space-y-4 pt-4">
            {/* Food Type */}
            <div className="space-y-2">
              <Label>{t('nutrition.foodType')} *</Label>
              <Select
                value={formData.food_type}
                onValueChange={(value: FoodType) => setFormData(prev => ({ ...prev, food_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FOOD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {t(type.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Brand with Autocomplete */}
            <div className="space-y-2">
              <Label>{t('nutrition.brand')}</Label>
              <BrandAutocomplete
                value={formData.brand}
                onChange={(value) => setFormData(prev => ({ ...prev, brand: value }))}
                isPrescription={formData.food_type === 'prescription'}
              />
            </div>

            {/* Product Name */}
            <div className="space-y-2">
              <Label>{t('nutrition.productName')}</Label>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                placeholder={t('nutrition.productNamePlaceholder')}
              />
            </div>

            {/* Feeding Frequency */}
            <div className="space-y-2">
              <Label>{t('nutrition.feedingFrequency')} *</Label>
              <Select
                value={formData.feeding_frequency}
                onValueChange={(value: FeedingFrequency) => setFormData(prev => ({ ...prev, feeding_frequency: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FEEDING_FREQUENCIES.map(freq => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {t(freq.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Portion Size */}
            <div className="space-y-2">
              <Label>{t('nutrition.portionSize')}</Label>
              <Input
                value={formData.portion_size}
                onChange={(e) => setFormData(prev => ({ ...prev, portion_size: e.target.value }))}
                placeholder={t('nutrition.portionSizePlaceholder')}
              />
            </div>

            {/* Diet Start Date */}
            <div className="space-y-2">
              <Label>{t('nutrition.dietStartDate')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.diet_start_date && "text-muted-foreground",
                      dateValidationError && "border-destructive"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.diet_start_date ? (
                      format(formData.diet_start_date, "PPP")
                    ) : (
                      <span>{t('nutrition.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.diet_start_date || undefined}
                    onSelect={(date) => setFormData(prev => ({ ...prev, diet_start_date: date || null }))}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {dateValidationError && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{dateValidationError}</span>
                </div>
              )}
            </div>

            {/* Diet End Date */}
            <div className="space-y-2">
              <Label>{t('nutrition.dietEndDate')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.diet_end_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.diet_end_date ? (
                      format(formData.diet_end_date, "PPP")
                    ) : (
                      <span>{t('nutrition.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.diet_end_date || undefined}
                    onSelect={(date) => setFormData(prev => ({ ...prev, diet_end_date: date || null }))}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('nutrition.notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={t('nutrition.notesPlaceholder')}
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-3 pt-4">
              <div>
                {formData.id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" disabled={isDeleting}>
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('nutrition.deleteConfirmTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('nutrition.deleteConfirmDescription')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>
                          {t('common.delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={isSaving || !!dateValidationError}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('common.save')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <NutritionHistory petId={petId} onHistoryChange={loadHistory} />
        )}
      </DialogContent>
    </Dialog>
  );
};
